import React, { useMemo } from 'react';
import type { SeedKey } from '../../hooks/useSeedEffect';
import { useValuesDirty } from '../../hooks/useValuesDirty';
import { useTranslation } from 'react-i18next';
import '../../App.css';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import MultiSelect, { MultiSelectOption } from '../MultiSelect';
import type { SeasonOption } from '../../utils/seasonOptions';
import SchemaForm from '../../schemaForm/SchemaForm';
import type { SchemaFormSection, SchemaFieldProps } from '../../schemaForm/types';
import PricingTiersField, {
  PriceTier,
  PricingTiersValue,
  generateDefaultPrices,
  STANDARD_DURATIONS,
} from '../../schemaForm/fields/PricingTiersField';
import CalendarConfigField, {
  CalendarConfig,
  CalendarConfigDraft,
  calendarConfigFromDraft,
  draftFromCalendarConfig,
  emptyCalendarConfigDraft,
} from '../../schemaForm/fields/CalendarConfigField';
import MembershipTypeField from '../../schemaForm/fields/MembershipTypeField';
import EntryConfigField, { EntryConfigValue } from '../../schemaForm/fields/EntryConfigField';
import DivisionSchemeField from '../../schemaForm/fields/DivisionSchemeField';
import { DivisionScheme, coerceDivisionScheme, emptyDivisionScheme } from '../../schemaForm/fields/divisionScheme';

// Re-export the jsonb shapes consumed by callers / sibling components.
export type { PriceTier, CalendarConfig };
export type { DivisionScheme };

export type MembershipFormType = 'season' | 'calendar' | 'time' | 'entry';

export interface IMembershipFormData {
  name: string;
  description: string;
  type: MembershipFormType;
  yearlyPrice: number;
  prices: PriceTier[];
  calendarConfig?: CalendarConfig;
  entryCount?: number | null;
  /** Season-division split definition (fractions + per-segment prices). */
  divisionScheme?: DivisionScheme;
  /**
   * Seasons this reusable membership is offered in (M:N via season_memberships).
   * Only sent when the form is given `seasonOptions` to manage — otherwise the
   * backend leaves existing attachments untouched.
   */
  seasonIds?: number[];
  /**
   * When set, this membership is a course OPTION (its `courseId` scopes it to a
   * course's season). Context-fixed: threaded from the caller, never
   * user-editable. `null`/absent = a standalone membership.
   */
  courseId?: number | null;
}

interface IMembershipFormProps {
  initialData?: Partial<IMembershipFormData>;
  /** Record identity — seeds once per record instead of per object identity. */
  seedKey?: SeedKey;
  /** Reports whether the form still matches its seed (host modals guard on it). */
  onDirtyChange?: (dirty: boolean) => void;
  onSubmit: (data: IMembershipFormData) => Promise<void>;
  loading?: boolean;
  submitButtonText: string;
  onDelete?: () => void;
  deleteButtonText?: string;
  courseId?: number | null;
  /**
   * Active season term used to render the DivisionSchemeField live preview
   * (concrete date windows + prices). Omit for a term-less (fraction-only) view.
   * Ignored when `previewSeasons` is provided (the selector drives the term).
   */
  previewTermStart?: string | null;
  previewTermEnd?: string | null;
  /**
   * Seasons the standalone DivisionSchemeField may preview its splits against.
   * When provided (and not season-bound), a "Preview against season" selector is
   * shown instead of a fixed term. Preview only — never persisted.
   */
  previewSeasons?: SeasonOption[];
  /** Which `previewSeasons` entry to preselect (e.g. the first attached season). */
  defaultPreviewSeasonId?: number;
  /** Warn when the membership is reused across seasons (editing splits affects all). */
  usedBySeasonCount?: number;
  /**
   * Seasons the admin can attach this membership to. When provided, a season
   * MultiSelect is shown for `season` memberships and `seasonIds` is sent on
   * submit (delete-then-insert reconcile on the backend).
   */
  seasonOptions?: MultiSelectOption[];
  /**
   * Season-bound OPTION mode (course-edit Payments tab). The membership is a
   * season-division option whose season is FIXED by the endpoint. When set, the
   * form forces `type='season'`, shows the DivisionSchemeField (with this
   * season's term driving the live preview), hides the `seasonIds` MultiSelect,
   * and submits `{ name, description, type:'season', divisionScheme, yearlyPrice }`
   * (no `seasonIds`, no `courseId`). Mutually exclusive with `courseId`.
   */
  seasonBound?: { id: number; termStart: string | null; termEnd: string | null };
}

/**
 * Schema-driven membership form. SchemaForm owns `name`/`description`/`type`,
 * while the jsonb shapes are handled by dedicated override widgets:
 *  - `type`           → MembershipTypeField (radio-card selector)
 *  - `divisionScheme` → DivisionSchemeField (season multi-lane pricing)
 *  - `prices`         → PricingTiersField   (yearly price + tiers, time type)
 *  - `calendarConfig` → CalendarConfigField (start month + intervals, calendar)
 *  - `entryCount`     → EntryConfigField    (entry count + price, entry type)
 *
 * New memberships are created as `type='season'`; `time`/`entry` remain for the
 * course-option path, and `calendar` is legacy (editable but not offered for new
 * creation). The right editor is shown per `type` via SchemaForm `visibility`.
 */
const MembershipForm: React.FC<IMembershipFormProps> = ({
  initialData,
  seedKey,
  onDirtyChange,
  onSubmit,
  loading = false,
  submitButtonText,
  onDelete,
  deleteButtonText,
  courseId = null,
  previewTermStart = null,
  previewTermEnd = null,
  previewSeasons,
  defaultPreviewSeasonId,
  usedBySeasonCount = 0,
  seasonOptions,
  seasonBound,
}) => {
  const { t } = useTranslation();

  const isSeasonBound = seasonBound != null;
  const initialType = initialData?.type;
  // A legacy course (time/entry) option is scoped by the deprecated
  // `memberships.course_id` scalar. A `season`-type membership is NEVER a legacy
  // course option (its scope is the M:N season_memberships junction), so a stale
  // course_id left on a season membership during cutover must not flip the
  // standalone editor into time/entry mode and hide the season settings.
  const isCourseOption = !isSeasonBound && courseId != null && initialType !== 'season';
  const managesSeasons = Array.isArray(seasonOptions);

  // The DivisionSchemeField preview is driven either by a FIXED term (season-
  // bound modal) or, in the standalone editor, by a "Preview against season"
  // selector. When a preview-season list is supplied we pass NO fixed term so the
  // selector takes over; otherwise fall back to the explicit preview term props.
  const hasPreviewList = !isSeasonBound && Array.isArray(previewSeasons) && previewSeasons.length > 0;
  const effectivePreviewStart = seasonBound?.termStart ?? (hasPreviewList ? null : previewTermStart);
  const effectivePreviewEnd = seasonBound?.termEnd ?? (hasPreviewList ? null : previewTermEnd);
  const effectivePreviewSeasons = hasPreviewList ? previewSeasons : undefined;

  const initialValues = useMemo(() => {
    const yearlyPrice = initialData?.yearlyPrice ?? 0;
    const seededPrices = initialData?.prices && initialData.prices.length > 0;
    const pricesValue: PricingTiersValue = {
      yearlyPrice,
      prices: seededPrices
        ? (initialData!.prices as PriceTier[]).map((tier) => ({
            ...tier,
            isCustom: tier.isCustom ?? !STANDARD_DURATIONS.includes(tier.durationDays),
          }))
        : generateDefaultPrices(yearlyPrice),
      manuallyEdited: !!seededPrices,
    };
    const calendarValue: CalendarConfigDraft = initialData?.calendarConfig
      ? draftFromCalendarConfig(initialData.calendarConfig)
      : emptyCalendarConfigDraft();
    const entryValue: EntryConfigValue = {
      entryCount: initialData?.entryCount ?? 0,
      yearlyPrice,
    };
    const divisionValue: DivisionScheme = initialData?.divisionScheme
      ? coerceDivisionScheme(initialData.divisionScheme, yearlyPrice)
      : emptyDivisionScheme(yearlyPrice);
    // Course options are term-scoped `time`; standalone memberships default to
    // `season` (the season-division model supersedes calendar for new creation).
    const defaultType: MembershipFormType = isCourseOption ? 'time' : 'season';
    return {
      name: initialData?.name ?? '',
      description: initialData?.description ?? '',
      type: initialData?.type ?? defaultType,
      prices: pricesValue,
      calendarConfig: calendarValue,
      entryCount: entryValue,
      divisionScheme: divisionValue,
      seasonIds: (initialData?.seasonIds ?? []).map(Number),
    };
  }, [initialData, isCourseOption]);

  const handleValuesChange = useValuesDirty(initialValues, onDirtyChange);

  const visibility = useMemo(
    () => ({
      divisionScheme: (values: Record<string, any>) => !isCourseOption && values.type === 'season',
      prices: (values: Record<string, any>) => values.type === 'time',
      calendarConfig: (values: Record<string, any>) => !isCourseOption && values.type === 'calendar',
      entryCount: (values: Record<string, any>) => values.type === 'entry',
      seasonIds: (values: Record<string, any>) => managesSeasons && values.type === 'season',
    }),
    [isCourseOption, managesSeasons]
  );

  // Narrow the type selector. Course options: time/entry. Standalone: season
  // only for new creation, plus the persisted legacy type when editing one.
  const typeEnumValues = useMemo<MembershipFormType[]>(() => {
    // Season-bound options are always season-division (the season is endpoint-fixed).
    if (isSeasonBound) return ['season'];
    if (isCourseOption) return ['time', 'entry'];
    const base: MembershipFormType[] = ['season'];
    if (initialType && initialType !== 'season' && !base.includes(initialType)) {
      base.push(initialType);
    }
    return base;
  }, [isSeasonBound, isCourseOption, initialType]);

  const TypeField = useMemo(
    () =>
      (props: SchemaFieldProps) => (
        <MembershipTypeField {...props} field={{ ...props.field, enumValues: typeEnumValues }} />
      ),
    [typeEnumValues]
  );

  const DivisionField = useMemo(
    () =>
      (props: SchemaFieldProps) => (
        <DivisionSchemeField
          {...props}
          termStart={effectivePreviewStart}
          termEnd={effectivePreviewEnd}
          previewSeasons={effectivePreviewSeasons}
          defaultPreviewSeasonId={defaultPreviewSeasonId}
          usedBySeasonCount={usedBySeasonCount}
        />
      ),
    [effectivePreviewStart, effectivePreviewEnd, effectivePreviewSeasons, defaultPreviewSeasonId, usedBySeasonCount]
  );

  const SeasonIdsField = useMemo(
    () =>
      (props: SchemaFieldProps) => (
        <FormField label={t('memberships.offeredInSeasons')} htmlFor="sf-membership-seasonIds">
          <MultiSelect
            id="sf-membership-seasonIds"
            options={seasonOptions ?? []}
            selectedIds={(props.value as number[]) ?? []}
            onChange={(ids) => props.onChange(ids.map(Number))}
            placeholder={t('memberships.offeredInSeasonsPlaceholder')}
          />
        </FormField>
      ),
    [seasonOptions, t]
  );

  const overrides = useMemo(
    () => ({
      type: TypeField,
      divisionScheme: DivisionField,
      prices: PricingTiersField,
      calendarConfig: CalendarConfigField,
      entryCount: EntryConfigField,
      seasonIds: SeasonIdsField,
    }),
    [TypeField, DivisionField, SeasonIdsField]
  );

  const sections: SchemaFormSection[] = useMemo(
    () => [
      {
        items: [
          'name',
          'description',
          'type',
          'divisionScheme',
          'seasonIds',
          'prices',
          'calendarConfig',
          'entryCount',
        ],
      },
    ],
    []
  );

  const handleSubmit = (values: Record<string, any>) => {
    const type = (values.type ?? 'season') as MembershipFormType;
    const pricesValue = values.prices as PricingTiersValue;
    const entryValue = values.entryCount as EntryConfigValue;
    const divisionValue = values.divisionScheme as DivisionScheme;

    // For season memberships the whole-season fullPrice is the canonical yearly
    // price; otherwise it comes from the entry or pricing-tier editor.
    const yearlyPrice =
      type === 'season'
        ? divisionValue?.fullPrice ?? 0
        : type === 'entry'
          ? entryValue.yearlyPrice
          : pricesValue.yearlyPrice;

    const data: IMembershipFormData = {
      name: values.name,
      description: values.description ?? '',
      type,
      yearlyPrice,
      prices: pricesValue.prices.map(({ isCustom, ...tier }) => tier),
      courseId,
    };

    if (!isCourseOption && type === 'season') {
      data.divisionScheme = divisionValue;
      if (managesSeasons) {
        data.seasonIds = ((values.seasonIds as number[]) ?? []).map(Number);
      }
    }
    if (!isCourseOption && type === 'calendar') {
      data.calendarConfig = calendarConfigFromDraft(values.calendarConfig as CalendarConfigDraft);
    }
    if (type === 'entry') {
      data.entryCount = entryValue.entryCount;
    }

    return onSubmit(data);
  };

  return (
    <SchemaForm
      entity="membership"
      sections={sections}
      overrides={overrides}
      visibility={visibility}
      initialValues={initialValues}
      seedKey={seedKey}
      onValuesChange={handleValuesChange}
      onSubmit={handleSubmit}
      submitLabel={submitButtonText}
      loading={loading}
      secondaryAction={
        onDelete ? (
          <Button type="button" variant="danger" fullWidth onClick={onDelete}>
            {deleteButtonText || t('common.delete')}
          </Button>
        ) : undefined
      }
    />
  );
};

export default MembershipForm;
